import React, { useState, useEffect } from 'react';
import {
  Form, Input, Select, Row, Col, Button, Space, Modal, DatePicker, Upload,
  Image, Progress,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import { RuleObject } from 'antd/lib/form';
import { StoreValue } from 'antd/lib/form/interface';

import { useSelector } from 'react-redux';
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const { TextArea } = Input;
const { Option } = Select;

interface UploadedFile {
  file_path: string;
  file_name: string;
}


const prefixSelector = (
  <Form.Item name="prefix" noStyle>
    <Select style={{ width: 100 }} dropdownStyle={{ borderRadius: 10 }}>
      <Option value="91">+91</Option>
      <Option value="977">+977</Option>
      <Option value="94">+94</Option>
      <Option value="7">+7</Option>
    </Select>
  </Form.Item>
);

// Helper to convert file to base64 for preview
const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

interface UserModelProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: {
    user_id?: number;
    user_name?: string;
    user_address?: string;
    user_country?: number;
    user_mobile?: number;
    user_email?: string;
    user_dob?: string;
    user_role?: string | number;
    user_image?: string;
    user_password?: string;
    user_status?: number;
    user_organization_id?: string | null;
  } | null;
  isEditMode?: boolean;
}

// password validation 
const validatePasswordStrength = (password: string) => {
  if (password.length < 6) return 0;
  let score = 0;
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return (score / 4) * 100;
};

interface Organizations {
  organization_id: string;
  organization_name: string;
  organization_category: string;
}
const UserModel: React.FC<UserModelProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const user = useSelector((state: any) => state.user?.user);
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  // For Upload component state
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [organizations, setOrganizations] = useState<Organizations[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [password, setPassword] = React.useState('');
  const formData = new FormData();

  const baseRoles = [
    { value: 'admin', label: 'Admin' },
    // { value: 'teacher', label: 'Teacher' },
    // { value: 'parent', label: 'Parent' },
    { value: 'user', label: 'User' },
    { value: 'staff', label: 'Staff' },
    { value: 'employee', label: 'Employee' },
  ];

  const extraRoles = [
    { value: 'super-master', label: 'Super Master' },
    { value: 'master', label: 'Master' },
  ];
  const roleOptions = user?.user_role === 'super-master'
    ? [...extraRoles, ...baseRoles]
    : baseRoles;
  // Disable submit if form errors exist
  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0); // If there are any validation errors, disable the button
  };

  const fetchOrganization = async () => {
    try {
      const data = await apiRequest<Organizations[]>({
        method: 'get',
        url: 'organization',
      });
      setOrganizations(data);
    } catch (error) {
      showError('Failed to fetch organization');
      console.error(error);
    }
  };

  // Handle file changes, update fileList and form value for
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    const updatedList = newFileList.map(file => {
      if (!file.url && !file.preview) {
        file.preview = URL.createObjectURL(file.originFileObj as Blob);
      }
      return file;
    });
    setFileList(updatedList);
  };

  // Upload button for Upload component
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // image preview
  const handlePreview = async (file: any) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });

  // Submit handler
  const handleAddUser = async (values: any) => {
    try {
      const formData = new FormData();

      formData.append('user_name', values.user_name);
      formData.append('user_address', values.user_address);
      formData.append('user_country', values.user_country);
      formData.append('user_mobile', values.user_mobile);
      formData.append('user_email', values.user_email);
      formData.append('user_dob', values.user_dob ? values.user_dob.format('YYYY-MM-DD') : '');
      formData.append('user_role', values.user_role);
      formData.append('user_password', values.user_password);
      formData.append('user_status', values.user_status);
      if (user?.user_role === 'super-master') {
        formData.append('user_organization_id', values.user_organization_id);
      }

      // Append the single image file

      if (fileList.length > 0 && fileList[0]?.originFileObj) {
        formData.append('attachments', fileList[0].originFileObj); // Use the correct field name for backend
      }


      if (initialValues?.user_id) {
        formData.append('user_id', initialValues.user_id.toString());
      }

      const url = isEditMode ? 'edit-user' : 'add-user';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onCreate(data);
      showSuccess(isEditMode ? 'User updated successfully!' : 'User created successfully!');
      form.resetFields();
      setFileList([]);

      if (isModalClose) {
        onCancel();
      }
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} user`);
    }
  };


  // On modal open or edit mode changes, set form values and fileList for user_image preview
  useEffect(() => {
    if (visible) {
      if (isEditMode && initialValues) {
        form.setFieldsValue({
          user_name: initialValues.user_name,
          user_address: initialValues.user_address,
          user_country: initialValues.user_country,
          user_mobile: initialValues.user_mobile,
          user_email: initialValues.user_email,
          user_dob: initialValues.user_dob ? dayjs(initialValues.user_dob) : undefined,
          user_role: initialValues.user_role,
          user_status: initialValues.user_status,
          user_image: initialValues.user_image,
          ...(user?.user_role === 'super-master' && {
            user_organization_id: initialValues.user_organization_id,
          }),
        });

        // If user_image exists, show in fileList for preview
        if (initialValues.user_image) {
          setFileList([
            {
              uid: '-1',
              name: 'image.png',
              status: 'done',
              url: `${process.env.REACT_APP_FILE_URL}uploads/users/profile-image/${initialValues.user_image}`,
            },
          ]);
        } else {
          setFileList([]);
        }
      } else {
        form.resetFields();
        setFileList([]);
      }
    }
    if (user?.user_role === 'super-master') {
      fetchOrganization()
    }
  }, [visible, isEditMode, initialValues, form]);

  // / Prevent automatic upload
  const beforeUpload = (file: File) => {
    const isAllowedType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);

    if (!isAllowedType) {
      showError('Only JPG/PNG/WEBP files are allowed!');
      return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      showError('Image must be smaller than 2MB!');
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  return (
    <Modal
      title={isEditMode ? 'Edit User' : 'Add New User'}
      visible={visible}
      onCancel={onCancel}
      width="90%"
      destroyOnHidden
      centered
      bodyStyle={{
        maxHeight: '70vh',     // Limit height of modal body
        overflowY: 'auto',     // Enable vertical scrolling
        paddingRight: '12px',  // Avoid scrollbar overlap
      }}
      footer={(
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '10px 24px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
                disabled={isSubmitDisabled()}
                form="userForm"
              >
                Submit & Create New User
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
              form="userForm"
            >
              Submit
            </Button>
          </Space>
        </div>
      )}
    >
      <Form
        id="userForm"
        form={form}
        layout="vertical"
        onFinish={handleAddUser}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="user_name"
              className="px-8"
              label={
                <span>
                  User Name&nbsp; {initialValues?.user_id}
                  <span style={{ fontSize: '12px', color: '#888' }}>(Max 255 characters)</span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the User Name' },
                { max: 255, message: 'Maximum 255 characters allowed' },
              ]}
            >
              <Input showCount maxLength={255} placeholder="e.g., User" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="user_country"
              label="User Country"
              className="px-8"
              rules={[]}
            >
              <Select
                showSearch
                allowClear
                placeholder="e.g., India"
                options={[
                  { value: '91', label: 'India' },
                  { value: '977', label: 'Nepal' },
                  { value: '94', label: 'Shree Lanka' },
                  { value: '7', label: 'Russia' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="user_mobile"
              label="User Mobile"
              className="px-8"
              rules={[
                { required: true, message: 'Please input your phone number!' },
                { pattern: /^\d{10}$/, message: 'Phone number must be 10 digits' },
              ]}
            >
              <Input
                className="custom-phone-input"
                addonBefore={prefixSelector}
                maxLength={10}
                onKeyPress={(e) => {
                  // Only allow digits (0-9)
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  // Clean input on paste or non-keyboard input
                  const cleaned = e.target.value.replace(/\D/g, '');
                  form.setFieldsValue({ user_mobile: cleaned });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="user_email"
              label="E-mail"
              className="px-8"
              rules={[
                { type: 'email', message: 'The input is not valid E-mail!' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 1: DOB | Role | (empty placeholder for layout consistency) */}
        <Row gutter={16}>
          {/* Left Column Group - 2 Rows inside Col */}
          <Col span={18}>
            {/* Top Row inside left column group */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="user_dob"
                  label="Date of Birth"
                  className="px-8"
                  rules={[{ required: true, message: 'Please select Date of Birth' }]}
                >
                  <DatePicker className="py-11" style={{ width: '100%', borderRadius: '20px' }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="user_role"
                  label="User Role"
                  className="px-8"
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="e.g., user or staff"
                    options={roleOptions}
                  />
                </Form.Item>
              </Col>
              {user?.user_role === 'super-master' && (
                <Col span={24}>
                  <Form.Item
                    name="user_organization_id"
                    label="Organization"
                    className="px-8"
                  >
                    <Select placeholder="Select Class" allowClear>
                      {organizations.map((org) => (
                        <Select.Option key={org.organization_id} value={org.organization_id}>
                          {org.organization_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
            </Row>

            {/* Bottom Row inside left column group */}
            {!isEditMode && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="user_password"
                    label="Password"
                    className="px-8"
                    rules={[
                      { required: true, message: 'Please input your password!' },
                      { min: 6, message: 'Password must be at least 6 characters' },
                    ]}
                    hasFeedback
                  >
                    <Input.Password
                      placeholder="Enter password"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Form.Item>

                  {password && (
                    <Form.Item className="px-8">
                      <Progress
                        percent={validatePasswordStrength(password)}
                        showInfo={false}
                        strokeColor={
                          validatePasswordStrength(password) < 50 ? '#f5222d' : '#52c41a'
                        }
                        status="active"
                      />
                      <div style={{ color: '#888', fontSize: 12 }}>
                        {validatePasswordStrength(password) < 50
                          ? 'Weak password'
                          : 'Strong password'}
                      </div>
                    </Form.Item>
                  )}
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="confirm"
                    label="Confirm Password"
                    dependencies={['user_password']}
                    className="px-8"
                    hasFeedback
                    rules={[
                      { required: true, message: 'Please confirm your password!' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('user_password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Passwords do not match!'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="Re-enter password" />
                  </Form.Item>
                </Col>
              </Row>
            )}

          </Col>

          {/* Right column - Upload Image Centered Vertically */}
          <Col
            span={6}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Form.Item
              // name="user_image"
              label="User Image"
              rules={[
                { required: !isEditMode, message: 'Please upload a user image!' },
              ]}
            >
              <Upload
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={({ fileList }) => setFileList(fileList)}
                beforeUpload={() => false} // prevent automatic upload
                maxCount={1} // only one image
                showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                accept="image/jpeg,image/png,image/webp"
              >
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>

              {previewImage && (
                <Image
                  wrapperStyle={{ display: 'none' }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) => !visible && setPreviewImage(''),
                  }}
                  src={previewImage}
                />
              )}

            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="user_address"
              className="px-8"
              label={
                <span>
                  Address&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>(Max 1000 characters)</span>
                </span>
              }
              rules={[]}
            >
              <TextArea maxLength={1000} />
            </Form.Item>
          </Col>
        </Row>


        <Modal
          visible={previewOpen}
          title={previewTitle}
          footer={null}
          onCancel={() => setPreviewOpen(false)}
        >
          <Image alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </Form>
    </Modal>
  );
};

export default UserModel;
